import React, { useCallback, useMemo, useRef } from 'react';

import { API } from 'constants';
import client from '../../../services/assistant-api';

import { useResponsiveBreakpoints } from '../../../hooks/useResponsiveBreakpoints';
import { IS_STREAM_LOADING_KEY } from '../../../hooks/useAssistantMessageStream';

import { defaultErrorMessage } from '../../../constants/errorMessages';
import { CHAT_ROLE, CHAT_TYPE } from '../../../constants/assistant';

import ContentLoader from '../ContentLoader/ContentLoader';
import MessageWrapper from '../MessageWrapper/MessageWrapper';
import MessageErrorBanner from '../MessageErrorBanner/MessageErrorBanner';
import DiviBotMessageHeader from '../DiviBotMessageHeader/DiviBotMessageHeader';
import CustomBotMessageHeader from '../CustomBotMessageHeader/CustomBotMessageHeader';
import AssistantMessageContent from '../AssistantMessageContentWithFeedback/AssistantMessageContentWithFeedback';
import AssistantMessageSourcesBlock from '../AssistantMessageSourcesBlock/AssistantMessageSourcesBlock';
import AssistantMessageActionButtons from '../AssistantMessageActionButtons/AssistantMessageActionButtons';

// possible statuses: "failure", "pending", "success"
const AssistantMessage = ({
    message,
    setChatMessages,
    isRegenerateDisabled,
    handleRegenerateChatMessage,
    isSimplifiedVersion = false,
}) => {
    const {
        id,
        default_version: {
            id: currentVersionId,
            content,
            status,
            tools,
            feedback,
            type: chatType,
            custom_bot_version,
            streamErrorMessage,
        },
    } = message;
    const ref = useRef(null);

    const isCompletedMessage = status === 'success';
    const isClickableErrorBanner = status === 'failure';

    // sometimes pending status is shown in old messages by mistake
    const isStreamLoading = message.default_version[IS_STREAM_LOADING_KEY];
    const isLoaderShown = status === 'pending' && !content && isStreamLoading;

    const toolsArray = useMemo(() => Object.entries(tools || {}), [tools]);

    const onRegenerateMessageClick = () => {
        if (handleRegenerateChatMessage) {
            handleRegenerateChatMessage({ id, role: CHAT_ROLE.assistant });
        }
    };

    const onSuccessFeedbackShare = useCallback(async () => {
        try {
            const { data } = await client.get(`${API.ROUTES.assistant.chatMessage}${id}/`);
            setChatMessages((prevMessages) =>
                prevMessages.map((message) => (message.id === data.id ? data : message))
            );
        } catch (e) {}
    }, [id, setChatMessages]);

    const withHeader = chatType === CHAT_TYPE.divibot || chatType === CHAT_TYPE.custom;

    const { isMobile } = useResponsiveBreakpoints();

    return (
        <MessageWrapper
            role={CHAT_ROLE.assistant}
            blackRobotIcon={chatType !== CHAT_TYPE.divibot}
            isSimplifiedVersion={isSimplifiedVersion}
        >
            {chatType === CHAT_TYPE.divibot && <DiviBotMessageHeader toolsArray={toolsArray} />}
            {chatType === CHAT_TYPE.custom && (
                <CustomBotMessageHeader customBotName={custom_bot_version?.custom_bot?.name} />
            )}
            <div className="flex flex-col gap-4" ref={ref}>
                {isLoaderShown && (
                    <ContentLoader additionalClassNames={withHeader ? '' : 'min-h-[24px]'} />
                )}

                {!isLoaderShown && (
                    <AssistantMessageContent
                        content={content}
                        currentVersionId={currentVersionId}
                        feedbackAllowed={
                            chatType === CHAT_TYPE.custom &&
                            isCompletedMessage &&
                            !isSimplifiedVersion
                        }
                        contentPieceId={currentVersionId}
                        feedbackTarget="chat_message_version"
                        feedbackTargetId={currentVersionId}
                        customToolTipPosition="top-left"
                        customFeedbackButtonType="neutral"
                        feedbackCount={feedback?.length || 0}
                        onSuccessFeedbackShare={onSuccessFeedbackShare}
                        feedbackSuccessAlertMessage="Feedback successfully submitted!"
                        selectionPopupTopOffset={isMobile ? 120 : 62}
                    />
                )}

                {isCompletedMessage && (
                    <AssistantMessageSourcesBlock
                        toolsArray={toolsArray}
                        currentVersionId={currentVersionId}
                        isSimplifiedVersion={isSimplifiedVersion}
                        onSuccessFeedbackShare={onSuccessFeedbackShare}
                        isCustomChatType={chatType === CHAT_TYPE.custom}
                    />
                )}

                {isCompletedMessage && (
                    <AssistantMessageActionButtons
                        message={message}
                        setChatMessages={setChatMessages}
                        isRegenerateDisabled={isRegenerateDisabled}
                        onRegenerateMessageClick={onRegenerateMessageClick}
                        containerRef={ref}
                        isSimplifiedVersion={isSimplifiedVersion}
                    />
                )}

                {isClickableErrorBanner && (
                    <MessageErrorBanner
                        errorMessage={streamErrorMessage || defaultErrorMessage}
                        handleRegenerate={onRegenerateMessageClick}
                        isDisabled={isRegenerateDisabled}
                    />
                )}
            </div>
        </MessageWrapper>
    );
};

export default AssistantMessage;
